import type {
  Data,
  Part,
  PartExpand,
  PartSchema,
  Schema,
  SeparatedParts,
} from 'schemaDefinition/types';
import type {PartVisitor} from './types';

export type ShouldVisitSubSchema = (
  part: PartSchema,
  parents: Part[] | undefined,
) => boolean;

/**
 * Visits all parts in schema and any subschemas.
 * Visitor will be called both for nested schema-parts and each of the
 * parts in the nested schemas
 * @param mutableSchema schema is mutable and may be adjusted by visitor
 * @param visitor
 * @param shouldVisitSubSchema
 * @param path default empty, but set when called recursively for sub-schemas
 * @param mutableParentParts if provided, parent parts will be pushed, passed to visitor, and popped
 */
export const visitSchemaParts = <TEntity extends Data = Data>(
  mutableSchema: Schema<TEntity, TEntity>,
  visitor: PartVisitor,
  shouldVisitSubSchema: ShouldVisitSubSchema = () => true,
  path = '',
  mutableParentParts: Part[] | undefined = undefined,
) => {
  visitParts(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    mutableSchema.parts,
    visitor,
    shouldVisitSubSchema,
    path,
    mutableParentParts,
  );
};

const visitPart = (
  part: Part,
  visitor: PartVisitor,
  shouldVisitSubSchema: ShouldVisitSubSchema,
  path: string,
  mutableParentParts: Part[] | undefined,
) => {
  const partPath = (
    path ? `${path}.${part.name as string}` : (part.name ?? '')
  ) as string;

  switch (part.type) {
    case 'expand': {
      visitExpandParts(
        part,
        visitor,
        shouldVisitSubSchema,
        path,
        mutableParentParts,
      );
      return;
    }
    case 'schema': {
      visitor(part, partPath, undefined, mutableParentParts);
      if (shouldVisitSubSchema(part, mutableParentParts)) {
        mutableParentParts?.push(part);
        visitSchemaParts(
          part,
          visitor,
          shouldVisitSubSchema,
          partPath,
          mutableParentParts,
        );
        mutableParentParts?.pop();
      }
      return;
    }
    default: {
      visitor(part, partPath, undefined, mutableParentParts);
      return;
    }
  }
};

const visitParts = (
  parts: SeparatedParts = [],
  visitor: PartVisitor,
  shouldVisitSubSchema: ShouldVisitSubSchema,
  path: string,
  mutableParentParts: Part[] | undefined,
) => {
  parts.forEach(part => {
    if (Array.isArray(part)) {
      visitParts(part, visitor, shouldVisitSubSchema, path, mutableParentParts);
    } else if (part.type !== 'separator' && part.type !== 'action') {
      visitPart(part, visitor, shouldVisitSubSchema, path, mutableParentParts);
    }
  });
};

export const visitExpandParts = (
  expand: PartExpand,
  visitor: PartVisitor,
  shouldVisitSubSchema: ShouldVisitSubSchema,
  path: string,
  mutableParentParts: Part[] | undefined,
) => {
  expand.when.forEach(w => {
    visitParts(
      w.parts,
      (part, partPath) => visitor(part, partPath, 'expand.when'),
      shouldVisitSubSchema,
      path,
      mutableParentParts,
    );
  });

  visitParts(
    expand.default,
    (part, partPath) => visitor(part, partPath, 'expand.default'),
    shouldVisitSubSchema,
    path,
    mutableParentParts,
  );
};
